import {
  productListSchema,
  productSchema,
  type CreateProductInput,
  type Product,
  type ProductCategory,
  type ProductList,
  type UpdateProductInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * Every catalogue call the app makes.
 *
 * Note what is absent: there is no "archive" route. Retiring a product is `active: false` through
 * the ordinary patch, because it is an ordinary edit — offering a separate verb here would imply
 * the server had one, and would make "stopped selling it" feel like a thing that cannot be undone.
 */

const PRODUCTS = '/products';

export interface ProductListParams {
  q?: string;
  category?: ProductCategory;
  /** Retired lines are hidden unless the screen asks for them. */
  includeRetired?: boolean;
}

export async function getProducts(
  client: ApiClient,
  params: ProductListParams = {},
): Promise<ProductList> {
  return await client.get(PRODUCTS, {
    schema: productListSchema,
    query: {
      ...(params.q ? { q: params.q } : {}),
      ...(params.category ? { category: params.category } : {}),
      ...(params.includeRetired ? { includeRetired: 'true' } : {}),
    },
  });
}

export async function getProduct(client: ApiClient, productId: string): Promise<Product> {
  return await client.get(`${PRODUCTS}/${productId}`, { schema: productSchema });
}

export async function createProduct(
  client: ApiClient,
  input: CreateProductInput,
): Promise<Product> {
  return await client.post(PRODUCTS, input, { schema: productSchema });
}

export async function updateProduct(
  client: ApiClient,
  productId: string,
  input: UpdateProductInput,
): Promise<Product> {
  return await client.patch(`${PRODUCTS}/${productId}`, input, { schema: productSchema });
}

export async function deleteProduct(client: ApiClient, productId: string): Promise<void> {
  await client.del(`${PRODUCTS}/${productId}`);
}
